import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Panel } from '@savgroup-front-common/core';
import { CrossIcon } from '@savgroup-front-common/core/src/protons/icons/Cross';

import { FileSummaryNewUi } from '../components/DossierFileSummary/FileSummaryNewUi';
import DossierDocumentsSection from '../components/DossierDocumentsSection';
import DossierHistorySection from '../components/DossierHistorySection';
import { DossierWorkflowBlock } from '../components/DossierWorkflowBlock';
import { ThumbZoneOverlay } from '../components/ThumbZoneOverlay';
import { DossierFileInfoProvider } from '../context/DossierFileInfoContext';
import { useInventoryThumbZone } from '../context/InventoryThumbZoneContext';
import { getDossierByFileId } from '../data/dossierMock';

import {
  $ClientMeta,
  $CloseButton,
  $HeaderRow,
  $HeaderText,
  $Page,
  $PanelInner,
  $Title,
  DOSSIER_RESPONSIVE_MAX_WIDTH_PX,
} from './DossierDetailPage.styles';

const DossierDetailPage = () => {
  const navigate = useNavigate();
  const { fileId } = useParams() as { fileId: string };
  const dossier = getDossierByFileId(fileId);
  const { isThumbZoneOverlayVisible, setThumbZoneOverlayVisible } = useInventoryThumbZone();
  const [isNarrowViewport, setIsNarrowViewport] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width: ${DOSSIER_RESPONSIVE_MAX_WIDTH_PX}px)`).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${DOSSIER_RESPONSIVE_MAX_WIDTH_PX}px)`);
    const onChange = () => {
      setIsNarrowViewport(mediaQuery.matches);
    };

    onChange();
    mediaQuery.addEventListener('change', onChange);

    return () => {
      mediaQuery.removeEventListener('change', onChange);
    };
  }, []);

  useEffect(() => {
    setThumbZoneOverlayVisible(false);
  }, [fileId, setThumbZoneOverlayVisible]);

  useEffect(() => {
    return () => {
      setThumbZoneOverlayVisible(false);
    };
  }, [setThumbZoneOverlayVisible]);

  const handleClose = useCallback(() => {
    navigate('/dossier');
  }, [navigate]);

  const header = (
    <$HeaderRow>
      <$HeaderText>
        <$Title level={2} withoutMargin>
          {dossier?.numeroDossier ?? 'Dossier introuvable'}
        </$Title>
        {dossier ? (
          <$ClientMeta>
            {dossier.clientPrenom} {dossier.clientNom} / {dossier.clientPhone}
          </$ClientMeta>
        ) : null}
      </$HeaderText>
      <$CloseButton
        type="button"
        onClick={handleClose}
        aria-label="Fermer le dossier"
        title="Fermer"
        data-testid="dossier-detail-close"
      >
        <CrossIcon size="22px" color="#334155" />
      </$CloseButton>
    </$HeaderRow>
  );

  const body = dossier ? (
    <DossierFileInfoProvider fileId={dossier.fileId} claimId={dossier.claimId}>
      <DossierWorkflowBlock fileId={dossier.fileId} />
      <FileSummaryNewUi />
      {isNarrowViewport ? (
        <>
          <DossierDocumentsSection fileId={dossier.fileId} />
          <DossierHistorySection fileId={dossier.fileId} />
        </>
      ) : null}
    </DossierFileInfoProvider>
  ) : null;

  const thumbOverlay = <ThumbZoneOverlay visible={isThumbZoneOverlayVisible} />;

  if (isNarrowViewport) {
    return (
      <$Page>
        {header}
        {body}
        {thumbOverlay}
      </$Page>
    );
  }

  return (
    <$Page>
      <Panel>
        <$PanelInner>
          {header}
          {body}
        </$PanelInner>
      </Panel>
      {thumbOverlay}
    </$Page>
  );
};

export default DossierDetailPage;
